export type TemplateMeasurementValues = Record<string, string>;
export interface TemplateOpening {
  id: string;
  kind: "window" | "door" | "rooflight";
  width: string;
  height: string;
  quantity: string;
  specification: string;
  deductFromWalls?: boolean;
}
export interface TemplateMeasurementField {
  id: string;
  label: string;
  unit: string;
  hint?: string;
  type?: "text";
}
const field = (id: string, label: string, unit: string, hint?: string): TemplateMeasurementField => ({ id, label, unit, hint });
const floor = field("floorArea", "Floor area", "m²", "Measured total area; overrides the footprint calculation.");
const walls = [
  field("wallLength", "Total wall length", "m", "Only the walls included in this work."),
  field("wallHeight", "Wall height", "m"),
  field("wallArea", "Net wall finish area", "m²", "Optional override after deducting openings."),
  field("ceilingArea", "Ceiling area", "m²"),
];
const finishes = [
  field("skirtingLength", "Skirting length", "m"),
  field("architraveLength", "Architrave length", "m"),
  field("windowBoardLength", "Window board length", "m"),
  field("tilingArea", "Wall tiling area", "m²"),
];
const roof = [
  field("roofArea", "Actual roof area", "m²", "Use the sloping surface area, not its plan footprint."),
  field("rooflineLength", "Fascia / gutter length", "m"),
  field("ridgeLength", "Ridge length", "m"),
];
const foundations = [
  field("foundationLength", "Foundation run", "m", "Actual trench run; do not count cavity wall leaves twice."),
  field("foundationWidth", "Foundation width", "mm", "Use the specified design dimensions."),
  field("foundationDepth", "Foundation concrete depth", "mm"),
  field("slabThickness", "Concrete slab thickness", "mm"),
];
const steel = [
  field("steelLength", "Steel cut length", "m", "Actual length, including specified bearings."),
  { id: "steelSection", label: "Steel section", unit: "", type: "text" as const, hint: "For example 254 × 254 × 73 UC; use the engineer's specification." },
];
const services = [
  field("socketCount", "Socket points", "each"),
  field("lightingCount", "Lighting points", "each"),
  field("radiatorCount", "Radiators", "each"),
  field("pipeLength", "Pipework length", "m"),
  field("drainageLength", "Drainage length", "m"),
];
export function getTemplateMeasurementFields(type: string): TemplateMeasurementField[] {
  switch (type) {
    case "extension": case "double_storey_extension": return [floor, ...walls, ...foundations, ...roof, ...steel, ...finishes, ...services];
    case "garage_conversion": case "freestanding_garage": return [floor, ...walls, ...foundations, ...finishes, ...steel, ...services];
    case "loft_conversion": return [floor, ...walls, ...roof, ...steel, ...finishes, ...services];
    case "bathroom_refurb": return [floor, ...walls, ...finishes, ...services];
    case "kitchen_refurb": return [floor, ...walls, field("kitchenRun", "Kitchen unit / worktop run", "m"), ...finishes, ...services];
    case "wall_opening": return [field("openingWidth", "Opening width", "mm"), field("openingHeight", "Opening height", "mm"), ...steel, field("wallArea", "Making-good area", "m²")];
    case "roofing": return roof;
    case "landscaping": return [
      field("pavingArea", "Paving area", "m²"), field("lawnArea", "Lawn area", "m²"), field("deckingArea", "Decking area", "m²"),
      field("fenceLength", "Fence length", "m"), field("subbaseDepth", "Sub-base depth", "mm"), ...services.filter(f => f.id === "drainageLength"),
    ];
    case "full_house_renovation": return [floor, ...walls, ...finishes, ...steel, ...services];
    case "garden_office": return [floor, ...walls, ...foundations, ...roof, ...finishes, ...services];
    default: return [];
  }
}
